import { useCallback, useEffect } from 'react'

import {
  FieldValues,
  UseFieldArrayReplace,
  UseFormGetValues,
  UseFormResetField,
  UseFormSetValue,
  useWatch,
} from 'react-hook-form'

import { Mode } from 'constants/index'
import {
  CongestionZone,
  useGetAutomaticCongestionZonesQuery,
  useGetBookingTariffQuery,
  useGetWaitAndReturnFeeCategoriesQuery,
  WaitAndReturnFeeCategory,
} from 'generated/graphql'
import { TBookingAddressForm, TBookingChargeForm, TBookingForm } from 'types/form-types'

import { useBookingCustomer } from '../useBookingCustomer'
import { useBookingData } from '../useBookingData'
import { useCongestionZonesChargeSync } from './useCongestionZonesChargeSync'
import { useDropChargeSync } from './useDropChargeSync'
import { useOutOfHoursChargeSync } from './useOutOfHoursChargeSync'
import { useWaitAndReturnChargeSync } from './useWaitAndReturnChargeSync'

type IAutomaticChargesSyncProps = {
  bookingId?: string
  tariffId?: string
  control: any
  getValues: UseFormGetValues<TBookingForm>
  replace: UseFieldArrayReplace<FieldValues, 'charges'>
  resetField: UseFormResetField<TBookingForm>
  setValue: UseFormSetValue<TBookingForm>
  mode?: number
}

export const useAutomaticBookingChargesSync = ({
  mode,
  bookingId,
  replace,
  control,
  getValues,
}: IAutomaticChargesSyncProps) => {
  const bookingData = useBookingData(bookingId)

  const [
    customerId,
    collectionAddresses = [],
    deliveryAddresses = [],
    tariffId,
    isWaitAndReturn,
    consignmentFee,
    waitAndReturnFeeCategoryId,
  ] = useWatch<TBookingForm>({
    control,
    name: [
      'customerId',
      'collectionAddresses',
      'deliveryAddresses',
      'tariffId',
      'isWaitAndReturn',
      'consignmentFee',
      'waitAndReturnFeeCategoryId',
    ],
  })

  const { customer } = useBookingCustomer(customerId)
  const isUlezCharge = customer?.isUlezCharge ?? false

  const { data: feeCategoriesData } = useGetWaitAndReturnFeeCategoriesQuery({
    fetchPolicy: 'cache-first',
  })
  const feeCategories = feeCategoriesData?.waitAndReturnFeeCategories as WaitAndReturnFeeCategory[]

  const { data: tariffData } = useGetBookingTariffQuery({
    fetchPolicy: 'cache-first',
    variables: { id: Number(tariffId) },
    skip: !tariffId,
  })
  const tariffDetails = tariffData?.tariffById

  const { data: zonesData } = useGetAutomaticCongestionZonesQuery({
    fetchPolicy: 'cache-first',
    variables: { tariffId: Number(tariffId) },
    skip: !tariffId,
  })
  const zones = zonesData?.automaticCongestionZones as CongestionZone[]

  const readCurrentCharges = useCallback(() => {
    return getValues('charges')
  }, [getValues])

  const replaceCurrentCharges = useCallback(
    (charges: TBookingChargeForm[]) => {
      replace(charges)
    },
    [replace],
  )

  const replaceIfChanged = useCallback(
    (before: TBookingChargeForm[], after: TBookingChargeForm[]) => {
      if (before.length !== after.length) {
        replaceCurrentCharges(after)
        return
      }
      const changed = after.some((c, i) => {
        const p = before[i]
        return (
          c.name !== p.name ||
          c.type !== p.type ||
          c.quantity !== p.quantity ||
          c.rate !== p.rate ||
          c.isDeleted !== p.isDeleted ||
          c.isAutomatic !== p.isAutomatic
        )
      })
      if (changed) {
        replaceCurrentCharges(after)
      }
    },
    [replaceCurrentCharges],
  )

  const isValidAddressesArray = useCallback((addresses: TBookingAddressForm[]): boolean => {
    const isValidAddress = (addr: TBookingAddressForm): boolean => {
      const hasValidBy =
        (typeof addr.by === 'string' && addr.by.trim() !== '') ||
        (addr.by instanceof Date && !isNaN(addr.by.getTime()))
      const hasValidAt =
        (typeof addr.at === 'string' && addr.at.trim() !== '') ||
        (addr.at instanceof Date && !isNaN(addr.at.getTime()))
      return hasValidAt || hasValidBy
    }
    return addresses.every(isValidAddress)
  }, [])

  const isValidRequiredAddressFields = useCallback((addresses: TBookingAddressForm[]): boolean => {
    return addresses.every((addr) => {
      return (
        typeof addr.name === 'string' &&
        addr.name.trim() !== '' &&
        typeof addr.city === 'string' &&
        addr.city.trim() !== '' &&
        typeof addr.addressLine1 === 'string' &&
        addr.addressLine1.trim() !== '' &&
        typeof addr.postcode === 'string' &&
        addr.postcode.trim() !== '' &&
        typeof addr.countryId === 'number' &&
        addr.countryId > 0
      )
    })
  }, [])

  const { handleOutOfHoursChargesSync } = useOutOfHoursChargeSync({
    collectionAddresses,
  })

  const { handleZonesChargesSync } = useCongestionZonesChargeSync({
    collectionAddresses,
    deliveryAddresses,
    zones,
    isUlezCharge,
  })

  const { handleDropChargeSync } = useDropChargeSync({
    tariffDetails,
    collectionAddresses,
    deliveryAddresses,
  })

  const { handleWaitAndReturnChargeSync } = useWaitAndReturnChargeSync({
    feeCategories,
    isWaitAndReturn,
    consignmentFee,
    waitAndReturnFeeCategoryId,
  })

  const isValidRequiredCollectionAddresses = isValidRequiredAddressFields(collectionAddresses)
  const isValidRequiredDeliveryAddresses = isValidRequiredAddressFields(deliveryAddresses)
  const isValidCollectionAddresses = isValidAddressesArray(collectionAddresses)
  const isValidDeliveryAddresses = isValidAddressesArray(deliveryAddresses)

  const isBookingDataIncomplete =
    bookingData.loading ||
    (mode === Mode.Update &&
      (bookingData.booking?.charges === undefined ||
        bookingData.booking?.isWaitAndReturn === undefined))

  const syncCharges = useCallback(
    (sync: (charges: TBookingChargeForm[]) => void) => {
      const charges = readCurrentCharges()
      const snapshot = charges.map((charge) => ({ ...charge }))

      sync(charges)

      replaceIfChanged(snapshot, charges)
    },
    [readCurrentCharges, replaceIfChanged],
  )

  useEffect(() => {
    if (isBookingDataIncomplete) {
      return
    }

    syncCharges((charges) => {
      if (isValidRequiredCollectionAddresses && isValidRequiredDeliveryAddresses) {
        handleWaitAndReturnChargeSync(charges)
        handleDropChargeSync(charges)
      }

      if (isValidCollectionAddresses || isValidDeliveryAddresses) {
        handleOutOfHoursChargesSync(charges)
        handleZonesChargesSync(charges)
      }
    })
  }, [
    handleDropChargeSync,
    handleOutOfHoursChargesSync,
    handleWaitAndReturnChargeSync,
    handleZonesChargesSync,
    isBookingDataIncomplete,
    isValidCollectionAddresses,
    isValidDeliveryAddresses,
    isValidRequiredCollectionAddresses,
    isValidRequiredDeliveryAddresses,
    syncCharges,
  ])
}
